import { FC, useCallback, useMemo, useState } from 'react';

import { CloseOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';

import MissingDetailsItem from '@/components/Common/MissingDetails/MissingDetailsItem';
import usePublicDomainCheck from '@/components/Common/hooks/usePublicDomainCheck';
import { ForwardingStatus } from '@/components/FilesTable/Modals/RevocationWarningModal/helpers';
import { SharedUserToDisplay } from '@/stores';
import RevocationPopover, { type RevocationType } from './RevocationPopover';
import styles from './PermissionDrawer.module.scss';

interface MissingDetailsRevokableItemProps {
    user: SharedUserToDisplay;
    allUsers: SharedUserToDisplay[];
    showPhoneColumn: boolean;
    showPasswordColumn: boolean;
    forwardingStatus: ForwardingStatus;
    onRevoke: (email: string, user: SharedUserToDisplay) => void;
    onRevokeAllDomain: (email: string) => void;
    onDetailsComplete: (email: string) => void;
}

const MissingDetailsRevokableItem: FC<MissingDetailsRevokableItemProps> = observer(({
    user,
    allUsers,
    showPhoneColumn,
    showPasswordColumn,
    forwardingStatus,
    onRevoke,
    onRevokeAllDomain,
    onDetailsComplete,
}) => {
    const [revokePopoverOpen, setRevokePopoverOpen] = useState(false);
    const { email } = user;
    const isGroup = user.recipientType === 'group';

    const { isPublicDomain } = usePublicDomainCheck(email);

    const revocationType: RevocationType = useMemo(() => {
        if (isPublicDomain) return 'simple';
        if (forwardingStatus.isForwardingEnabled || (forwardingStatus.isDomainForwardingEnabled && isGroup)) {
            return 'forwarding';
        }
        if (forwardingStatus.isDomainForwardingEnabled) {
            return 'domainForwarding';
        }
        return 'simple';
    }, [forwardingStatus, isGroup, isPublicDomain]);

    const domainUsers = useMemo(() => {
        if (revocationType !== 'domainForwarding') return [];
        const userDomain = email.split('@')[1]?.toLowerCase();
        if (!userDomain) return [];
        return allUsers
            .filter((u) => {
                const d = u.email.split('@')[1]?.toLowerCase();
                return d === userDomain && !u.deleted && u.email !== email;
            })
            .map((u) => u.email);
    }, [revocationType, email, allUsers]);

    const handleRevokeClick = useCallback(() => {
        setRevokePopoverOpen(true);
    }, []);

    const handleRevokeCancel = useCallback(() => {
        setRevokePopoverOpen(false);
    }, []);

    const handleRevokeContinue = useCallback(() => {
        setRevokePopoverOpen(false);
        onRevoke(email, user);
    }, [email, user, onRevoke]);

    const handleRevokeFromDomain = useCallback(() => {
        setRevokePopoverOpen(false);
        onRevokeAllDomain(email);
    }, [email, onRevokeAllDomain]);

    return (
        <div
            className={styles['missing-details-item']}
            data-testid={`permissionDrawer_missingDetailsItem_${user.userId}`}
        >
            <MissingDetailsItem
                email={email}
                showPhones={showPhoneColumn}
                showPasswords={showPasswordColumn}
                onDetailsComplete={() => onDetailsComplete(email)}
                emailClassName={styles['missing-details-email']}
                passwordBlockClassName={styles['missing-details-password-block']}
                renderLayout={(inputs, emailLabel) => (
                    <div className={styles['missing-details-inputs']}>
                        {inputs}
                        <div className={styles['missing-details-email-row']}>
                            {emailLabel}
                            <RevocationPopover
                                open={revokePopoverOpen}
                                onOpenChange={(open) => {
                                    if (!open) setRevokePopoverOpen(false);
                                }}
                                type={revocationType}
                                userEmail={email}
                                isGroup={isGroup}
                                domainUsers={domainUsers}
                                onCancel={handleRevokeCancel}
                                onContinue={handleRevokeContinue}
                                onRevokeFromDomain={revocationType === 'domainForwarding' ? handleRevokeFromDomain : undefined}
                            >
                                <CloseOutlined
                                    className={styles['missing-details-revoke']}
                                    onClick={handleRevokeClick}
                                    data-testid={`permissionDrawer_missingDetailsRevoke_${user.userId}`}
                                />
                            </RevocationPopover>
                        </div>
                    </div>
                )}
            />
        </div>
    );
});

export default MissingDetailsRevokableItem;
